// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Divider, Typography } from '@mui/material';
import { DateInput, Label, OptionsSelector } from '@shared/ui-kit';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Controller } from 'react-hook-form';
import {
  useDocumentsFiltersDrawer,
  type UseDocumentsFiltersDrawerProps,
} from './use-documents-filters-drawer';

export type DocumentsFiltersDrawerProps = UseDocumentsFiltersDrawerProps;

export const DocumentsFiltersDrawer = defineComponent({
  displayName: 'DocumentsFiltersDrawer',
  useSetup: useDocumentsFiltersDrawer,

  View: ({
    DrawerRenderer,
    onClose,
    t,
    control,
    handleSubmit,
    errors,
    isValid,
    categorySelectOptions,
    subcategorySelectOptions,
    languageSelectOptions,
    isSubcategoryDisabled,
    onCategoryChange,
    onClearDraft,
    onViewResults,
  }) => (
    <DrawerRenderer
      anchor="right"
      PaperProps={{
        classes: {
          root: '!bg-page !w-full !max-w-2xl',
        },
      }}
      data-testid="documents-filters-drawer"
    >
      <form
        className="flex min-h-full flex-col"
        onSubmit={handleSubmit(onViewResults)}
      >
        <div className="relative flex flex-col gap-1 p-7">
          <div className="absolute right-0 top-0 p-7">
            <IconButton
              icon="Close"
              variant="icon"
              size="md"
              color="main"
              aria-label={t('common.close', { ns: 'common' })}
              className="!rounded-lg"
              onClick={onClose}
              data-testid="documents-filters-drawer-close"
            />
          </div>

          <Typography
            variant="h6"
            component="h2"
            className="!pr-14 !text-xl !font-semibold !text-title-dark"
          >
            <Translation i18nKey="documents.filters-drawer.title" ns="components" />
          </Typography>
          <Typography
            variant="body2"
            className="!text-base !font-normal !text-primary-medium"
          >
            <Translation
              i18nKey="documents.filters-drawer.subtitle"
              ns="components"
            />
          </Typography>
        </div>

        <Divider />

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-7">
          <fieldset className="flex flex-col gap-2 border-0 p-0">
            <Label>
              <Translation
                i18nKey="documents.filters-drawer.category"
                ns="components"
              />
            </Label>
            <Controller
              control={control}
              name="category"
              render={({ field }) => (
                <OptionsSelector
                  optionsAvailable={[...categorySelectOptions]}
                  optionSelected={field.value}
                  onChangeValueSelected={value => {
                    field.onChange(value);
                    onCategoryChange(value);
                  }}
                  defaultText={t('documents.filters-drawer.all-categories', {
                    ns: 'components',
                  })}
                  selectType="white"
                  data-testid="documents-filters-category"
                />
              )}
            />
          </fieldset>

          <fieldset className="flex flex-col gap-2 border-0 p-0">
            <Label>
              <Translation
                i18nKey="documents.filters-drawer.subcategory"
                ns="components"
              />
            </Label>
            <Controller
              control={control}
              name="subcategory"
              render={({ field }) => (
                <OptionsSelector
                  optionsAvailable={[...subcategorySelectOptions]}
                  optionSelected={field.value}
                  onChangeValueSelected={field.onChange}
                  defaultText={t('documents.filters-drawer.all-subcategories', {
                    ns: 'components',
                  })}
                  selectType="white"
                  disabled={isSubcategoryDisabled}
                  data-testid="documents-filters-subcategory"
                />
              )}
            />
          </fieldset>

          <fieldset className="flex flex-col gap-2 border-0 p-0">
            <Label>
              <Translation
                i18nKey="documents.filters-drawer.language"
                ns="components"
              />
            </Label>
            <Controller
              control={control}
              name="language"
              render={({ field }) => (
                <OptionsSelector
                  optionsAvailable={[...languageSelectOptions]}
                  optionSelected={field.value}
                  onChangeValueSelected={field.onChange}
                  defaultText={t('documents.filters-drawer.all-languages', {
                    ns: 'components',
                  })}
                  selectType="white"
                  data-testid="documents-filters-language"
                />
              )}
            />
          </fieldset>

          <fieldset className="flex flex-col gap-3 border-0 p-0">
            <Label>
              <Translation
                i18nKey="documents.filters-drawer.document-date-range"
                ns="components"
              />
            </Label>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <fieldset className="flex flex-col gap-2 border-0 p-0">
                <Label>
                  <Translation
                    i18nKey="documents.filters-drawer.date-from"
                    ns="components"
                  />
                </Label>
                <Controller
                  control={control}
                  name="document_date_from"
                  render={({ field }) => (
                    <DateInput
                      value={field.value}
                      onChange={field.onChange}
                      error={Boolean(errors.document_date_from)}
                      data-testid="documents-filters-document-date-from"
                    />
                  )}
                />
              </fieldset>
              <fieldset className="flex flex-col gap-2 border-0 p-0">
                <Label>
                  <Translation
                    i18nKey="documents.filters-drawer.date-to"
                    ns="components"
                  />
                </Label>
                <Controller
                  control={control}
                  name="document_date_to"
                  render={({ field }) => (
                    <DateInput
                      value={field.value}
                      onChange={field.onChange}
                      error={Boolean(errors.document_date_to)}
                      data-testid="documents-filters-document-date-to"
                    />
                  )}
                />
              </fieldset>
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-3 border-0 p-0">
            <Label>
              <Translation
                i18nKey="documents.filters-drawer.expiry-date-range"
                ns="components"
              />
            </Label>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <fieldset className="flex flex-col gap-2 border-0 p-0">
                <Label>
                  <Translation
                    i18nKey="documents.filters-drawer.date-from"
                    ns="components"
                  />
                </Label>
                <Controller
                  control={control}
                  name="expiry_date_from"
                  render={({ field }) => (
                    <DateInput
                      value={field.value}
                      onChange={field.onChange}
                      error={Boolean(errors.expiry_date_from)}
                      data-testid="documents-filters-expiry-date-from"
                    />
                  )}
                />
              </fieldset>
              <fieldset className="flex flex-col gap-2 border-0 p-0">
                <Label>
                  <Translation
                    i18nKey="documents.filters-drawer.date-to"
                    ns="components"
                  />
                </Label>
                <Controller
                  control={control}
                  name="expiry_date_to"
                  render={({ field }) => (
                    <DateInput
                      value={field.value}
                      onChange={field.onChange}
                      error={Boolean(errors.expiry_date_to)}
                      data-testid="documents-filters-expiry-date-to"
                    />
                  )}
                />
              </fieldset>
            </div>
          </fieldset>

          {errors.document_date_from ||
          errors.document_date_to ||
          errors.expiry_date_from ||
          errors.expiry_date_to ? (
            <Typography
              variant="body2"
              className="!text-sm !text-negative"
              data-testid="documents-filters-invalid-date-range"
            >
              <Translation
                i18nKey="documents.filters-drawer.invalid-date-range"
                ns="components"
              />
            </Typography>
          ) : null}
        </div>

        <Divider />

        <div className="flex gap-4 p-7">
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={onClearDraft}
            className="h-10 flex-1 normal-case"
            data-testid="documents-filters-clear"
          >
            <Translation i18nKey="documents.filters-drawer.clear" ns="components" />
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={!isValid}
            className="h-10 flex-1 normal-case"
            data-testid="documents-filters-apply"
          >
            <Translation
              i18nKey="documents.filters-drawer.view-results"
              ns="components"
            />
          </Button>
        </div>
      </form>
    </DrawerRenderer>
  ),
});
